import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Autocomplete,
  Chip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useCollection } from '../hooks/useFirestore';
import { query as fbQuery, orderBy as fbOrderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, doc, serverTimestamp, deleteField } from 'firebase/firestore';
import BlockEditor, { ContentBlock } from '../components/blocks/BlockEditor';
import MediaLibraryDialog from '../components/blocks/MediaLibraryDialog';
import { renderBlocks } from '../components/blocks/renderBlocks';

interface FAQ {
  id: string;
  question: string;
  answer?: string;
  blocks?: ContentBlock[];
  // legacy
  target?: 'school' | 'student' | 'both' | 'lp';
  // new
  targets?: string[];
  categoryIds?: string[];
  order: number;
  status: 'visible' | 'hidden' | 'scheduled';
  publishedAt?: Date;
  createdAt: Date;
}

interface FAQCategory {
  id: string;
  name: string;
  order: number;
  status?: 'visible' | 'hidden';
}

const TARGET_PRESETS: { value: string; label: string }[] = [
  { value: 'school', label: '教室' },
  { value: 'student', label: '生徒' },
  { value: 'lp', label: 'LP' },
  { value: 'faqList', label: 'FAQ一覧' },
];

function uniq(arr: string[]): string[] {
  return Array.from(new Set(arr.filter(Boolean)));
}

function resolveTargetsFromDoc(faq: any): string[] {
  const v = faq?.targets;
  if (Array.isArray(v)) return uniq(v.map(String));
  const legacy = String(faq?.target || '');
  if (!legacy) return [];
  if (legacy === 'both') return ['school', 'student'];
  return [legacy];
}

function computeLegacyTargetFromTargets(targets: string[]): 'school' | 'student' | 'both' | 'lp' | undefined {
  // legacy target は旧クライアント互換用（school/student/lp のみで判定する）
  // 例: targets に 'faqList' のような新しい値が混ざっても互換が壊れないように無視する
  const legacyEligible = targets
    .map(String)
    .filter((x) => x === 'school' || x === 'student' || x === 'lp');
  const t = uniq(legacyEligible);
  if (t.length === 1) {
    const only = t[0];
    if (only === 'school' || only === 'student' || only === 'lp') return only;
    return undefined;
  }
  // school + student のみは legacy で表現可能
  if (t.length === 2 && t.includes('school') && t.includes('student')) return 'both';
  // それ以外（例: school+lp）は legacy では表現できないため、旧クライアントに誤表示させないように undefined
  return undefined;
}

export default function FAQEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: faqs } = useCollection<FAQ>(
    'faqs',
    (q) => fbQuery(q, fbOrderBy('order', 'asc'))
  );

  const { data: categories } = useCollection<FAQCategory>(
    'faqCategories',
    (q) => fbQuery(q, fbOrderBy('order', 'asc'))
  );

  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    blocks: [] as ContentBlock[],
    targets: ['school', 'student'] as string[],
    categoryIds: [] as string[],
    order: 0,
    status: 'visible' as 'visible' | 'hidden' | 'scheduled',
    publishedAt: new Date(),
  });

  const [mediaOpen, setMediaOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // 編集時は既存データを読み込み
  useEffect(() => {
    if (isEdit && faqs) {
      const faq = faqs.find(f => f.id === id);
      if (faq) {
        setFormData({
          question: faq.question,
          answer: faq.answer || '',
          blocks: (faq as any).blocks || [],
          targets: resolveTargetsFromDoc(faq),
          categoryIds: Array.isArray((faq as any).categoryIds) ? (faq as any).categoryIds.map((x: any) => String(x)) : [],
          order: faq.order,
          status: (faq as any).status || 'visible',
          publishedAt: ((): Date => {
            const v: any = (faq as any).publishedAt;
            if (!v) return new Date();
            try { return typeof v.toDate === 'function' ? v.toDate() : new Date(v); } catch { return new Date(); }
          })(),
        });
      }
    } else if (!isEdit && faqs) {
      // 新規作成時は次の順序を設定
      setFormData(prev => ({
        ...prev,
        order: (faqs?.length || 0) + 1,
      }));
    }
  }, [isEdit, id, faqs]);

  const handleSave = async () => {
    if (!formData.question || (!formData.answer && formData.blocks.length === 0)) {
      alert('質問と回答を入力してください');
      return;
    }
    if (!Array.isArray(formData.targets) || formData.targets.length === 0) {
      alert('表示対象を1つ以上選択してください');
      return;
    }

    setLoading(true);
    try {
      // undefined フィールドを除外
      const saveData: any = {
        question: formData.question,
        answer: formData.answer || '',
        blocks: formData.blocks || [],
        targets: uniq(formData.targets),
        categoryIds: uniq(Array.isArray(formData.categoryIds) ? formData.categoryIds : []),
        order: formData.order,
        status: formData.status,
      };

      // publishedAt が有効な場合のみ追加
      if (formData.publishedAt && formData.publishedAt instanceof Date) {
        saveData.publishedAt = formData.publishedAt;
      }

      // blocks配列内のundefinedフィールドも除外
      if (saveData.blocks && Array.isArray(saveData.blocks)) {
        saveData.blocks = saveData.blocks.map((block: any) => {
          if (block && typeof block === 'object') {
            return Object.fromEntries(
              Object.entries(block).filter(([_, value]) => value !== undefined)
            );
          }
          return block;
        });
      }

      // undefined フィールドを完全に除外
      const cleanSaveData = Object.fromEntries(
        Object.entries(saveData).filter(([_, value]) => value !== undefined)
      );

      // legacy target も「表現できる場合のみ」付与（表現できない組み合わせは旧クライアントに誤表示させない）
      const legacyTarget = computeLegacyTargetFromTargets(
        Array.isArray((cleanSaveData as any).targets) ? ((cleanSaveData as any).targets as string[]) : []
      );

      // デバッグ用 - より詳細な情報を出力
      console.log('=== DEBUG INFO ===');
      console.log('formData:', formData);
      console.log('Original saveData:', saveData);
      console.log('Clean saveData:', cleanSaveData);
      
      // 各フィールドの詳細チェック
      Object.entries(cleanSaveData).forEach(([key, value]) => {
        console.log(`${key}:`, value, `(type: ${typeof value})`);
        if (value === undefined) {
          console.error(`❌ UNDEFINED FIELD: ${key}`);
        }
        if (value === null) {
          console.warn(`⚠️ NULL FIELD: ${key}`);
        }
      });
      
      // blocks配列の詳細チェック
      if (cleanSaveData.blocks && Array.isArray(cleanSaveData.blocks)) {
        console.log('blocks array:', cleanSaveData.blocks);
        cleanSaveData.blocks.forEach((block: any, index: number) => {
          console.log(`block[${index}]:`, block);
          if (block && typeof block === 'object') {
            Object.entries(block).forEach(([blockKey, blockValue]) => {
              if (blockValue === undefined) {
                console.error(`❌ UNDEFINED IN BLOCK[${index}].${blockKey}:`, blockValue);
              }
            });
          }
        });
      }

      if (isEdit && id) {
        // 編集時は createdAt を除外
        const { createdAt, ...updateData } = cleanSaveData;
        const patch: any = { ...(updateData as any) };
        if (legacyTarget) patch.target = legacyTarget;
        else patch.target = deleteField();
        await updateDoc(doc(db, 'faqs', id), patch);
      } else {
        // 新規作成時は createdAt を serverTimestamp で設定
        const { createdAt, ...createData } = cleanSaveData;
        await addDoc(collection(db, 'faqs'), {
          ...createData,
          ...(legacyTarget ? { target: legacyTarget } : {}),
          createdAt: serverTimestamp(),
        });
      }
      navigate('/faqs');
    } catch (error) {
      console.error('Failed to save FAQ:', error);
      alert('保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const renderAnswerPreview = (answer: string, blocks?: ContentBlock[]) => {
    if (blocks && blocks.length > 0) {
      return (
        <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
          {renderBlocks(blocks)}
        </Box>
      );
    }
    const parts = (answer || '').split(/\n+/);
    return (
      <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
        {parts.map((line, idx) => {
          const urlMatch = line.match(/https?:\/\/[^\s]+/g);
          if (urlMatch && urlMatch.length === 1 && line.trim() === urlMatch[0]) {
            const url = urlMatch[0];
            const lower = url.toLowerCase();
            const isImage = lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.gif') || lower.endsWith('.webp');
            if (isImage) {
              return (
                <Box key={idx} sx={{ my: 1 }}>
                  <img src={url} alt="preview" style={{ maxWidth: '100%', borderRadius: 6 }} />
                </Box>
              );
            }
            return (
              <Box key={idx} sx={{ my: 0.5 }}>
                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
              </Box>
            );
          }
          return (
            <Typography key={idx} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{line}</Typography>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/faqs')}
          sx={{ mr: 2 }}
        >
          戻る
        </Button>
        <Typography variant="h4">
          {isEdit ? 'FAQ編集' : 'FAQ新規作成'}
        </Typography>
      </Box>

      <Card sx={{ p: 3 }}>
        <TextField
          fullWidth
          label="質問"
          value={formData.question}
          onChange={(e) => setFormData({ ...formData, question: e.target.value })}
          sx={{ mb: 3 }}
        />

        <Typography variant="h6" sx={{ mb: 2 }}>回答</Typography>
        
        {/* ブロックエディタ */}
        <BlockEditor
          value={formData.blocks}
          onChange={(next) => setFormData({ ...formData, blocks: next })}
          onPickImage={async () => {
            return new Promise((resolve) => {
              setMediaOpen(true);
              const handle = (e: any) => {
                window.removeEventListener('block-editor-media', handle);
                resolve(e.detail || null);
              };
              window.addEventListener('block-editor-media', handle, { once: true });
            });
          }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1, mt: 3 }}>プレビュー</Typography>
        {renderAnswerPreview(formData.answer, formData.blocks)}

        <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <FormControl sx={{ minWidth: 160, flex: '1 1 160px' }}>
            <InputLabel>ステータス</InputLabel>
            <Select
              value={formData.status}
              label="ステータス"
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'visible' | 'hidden' | 'scheduled' })}
            >
              <MenuItem value="visible">表示</MenuItem>
              <MenuItem value="hidden">非表示</MenuItem>
              <MenuItem value="scheduled">予約</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="公開日時"
            type="datetime-local"
            value={((): string => {
              const d = formData.publishedAt instanceof Date ? formData.publishedAt : new Date(formData.publishedAt as any);
              const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
              const yyyy = d.getFullYear();
              const MM = pad(d.getMonth() + 1);
              const dd = pad(d.getDate());
              const hh = pad(d.getHours());
              const mm = pad(d.getMinutes());
              return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
            })()}
            onChange={(e) => setFormData({ ...formData, publishedAt: new Date(e.target.value) })}
            sx={{ minWidth: 240, flex: '1 1 240px' }}
          />

          <Box sx={{ minWidth: 260, flex: '2 1 320px' }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
              表示対象（複数選択可）
            </Typography>
            <FormGroup row sx={{ flexWrap: 'wrap' }}>
              {uniq([
                ...TARGET_PRESETS.map((o) => o.value),
                ...(Array.isArray(formData.targets) ? formData.targets : []),
              ]).map((value) => {
                const label = TARGET_PRESETS.find((o) => o.value === value)?.label || value;
                const checked = Array.isArray(formData.targets) && formData.targets.includes(value);
                return (
                  <FormControlLabel
                    key={value}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          const nextChecked = e.target.checked;
                          const current = Array.isArray(formData.targets) ? formData.targets : [];
                          const next = nextChecked ? uniq([...current, value]) : current.filter((v) => v !== value);
                          setFormData({ ...formData, targets: next });
                        }}
                      />
                    }
                    label={label}
                  />
                );
              })}
            </FormGroup>
          </Box>

          <Box sx={{ minWidth: 320, flex: '2 1 360px' }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
              カテゴリ（複数選択可）
            </Typography>
            <Autocomplete
              multiple
              options={(categories || []).filter((c) => (c as any).status !== 'hidden')}
              getOptionLabel={(o) => o?.name || ''}
              value={(categories || []).filter((c) => Array.isArray(formData.categoryIds) && formData.categoryIds.includes(c.id))}
              onChange={(_, values) => {
                const ids = (values || []).map((v) => v.id);
                setFormData({ ...formData, categoryIds: uniq(ids) });
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                ))
              }
              renderInput={(params) => <TextField {...params} placeholder="カテゴリを選択" fullWidth />}
            />
          </Box>

          <TextField
            label="表示順序"
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
            sx={{ minWidth: 160, flex: '1 1 160px' }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/faqs')}
            disabled={loading}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !formData.question || (!formData.answer && formData.blocks.length === 0)}
          >
            {loading ? '保存中...' : (isEdit ? '更新' : '作成')}
          </Button>
        </Box>
      </Card>

      <MediaLibraryDialog
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={(item) => {
          window.dispatchEvent(new CustomEvent('block-editor-media', { detail: { url: item.url } }));
          setMediaOpen(false);
        }}
      />
    </Box>
  );
}
